import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="marketing hero-bg min-h-screen">
      <div className="mx-auto flex max-w-5xl items-center justify-center px-6 py-16 md:min-h-screen">
        <div className="grid w-full gap-10 md:grid-cols-2 md:items-center">
          <div className="hidden md:block">
            <div className="mb-6">
              <BrandLogo size="lg" />
              <div className="overline mt-2 text-muted-foreground">Advisory OS</div>
            </div>
            <h1 className="mb-4 text-4xl tracking-tight">Sign in to your practice.</h1>
            <p className="mb-8 max-w-md text-muted-foreground">
              Manage the five-stage journey — Discovery, Goal Mapping, Written Plan, Execution, Ongoing Review — for
              every client, in one place.
            </p>
          </div>
          <Card className="border-border bg-white p-8 shadow-sm">
            <div className="mb-8">
              <h2 className="mb-1 text-2xl font-dashboard font-bold tracking-tight">Advisor Sign In</h2>
              <p className="text-sm text-muted-foreground">Access the backoffice portal.</p>
            </div>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="login-submit">
                <LogIn className="h-4 w-4" /> {busy ? "Signing in…" : "Sign in"}
              </Button>
              <div className="pt-4 mt-2 border-t border-border text-center text-sm text-muted-foreground">
                Are you a client?{" "}
                <a href="/client-login" className="font-medium text-primary hover:underline" data-testid="client-login-link">
                  Log in here →
                </a>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
